import type { LucideIcon } from 'lucide-react';
import { Eye, Star, Smartphone, Trash2, UserPlus, Users } from 'lucide-react';
import { formatCompact } from '@/lib/money';
import type { DashboardOverviewResponseDTO, DashboardTimeseriesResponseDTO } from './api';

type OverviewWithPhase4 = DashboardOverviewResponseDTO & {
  visitorsDeltaPercent?: number | null;
  visitorsDataSource?: 'CACHE' | 'LIVE' | string | null;
  activeUsersDataSource?: 'CACHE' | 'LIVE' | string | null;
  postHogSyncedAt?: string | null;
};
import type { DashboardKpiCardProps } from './components/DashboardKpiCard';

const countFormatter = new Intl.NumberFormat('en-AU');

function formatKpiCount(n: number): string {
  if (Math.abs(n) >= 10_000) return formatCompact(n);
  return countFormatter.format(n);
}

function deltaNum(value: number | null | undefined): number | null {
  if (value == null || Number.isNaN(Number(value))) return null;
  return Number(value);
}

function seriesValues(
  timeseries: DashboardTimeseriesResponseDTO | undefined,
  key: string,
): number[] {
  const series = timeseries?.series?.find((s) => s.key === key);
  return (series?.values ?? []).map((v) => Number(v) || 0);
}

export type DashboardKpiIconTone = 'blue' | 'cyan' | 'pink' | 'purple' | 'rose';

export type DashboardKpi = Pick<
  DashboardKpiCardProps,
  'label' | 'value' | 'delta' | 'hint' | 'comingSoon' | 'unavailable' | 'icon' | 'iconTone'
>;

/** Figma glyph mapping for Platform Growth KPI cards. */
export const DASHBOARD_KPI_ICONS: Record<string, { icon: LucideIcon; tone: DashboardKpiIconTone }> =
  {
    Visitors: { icon: Eye, tone: 'blue' },
    'Lifetime Users': { icon: Users, tone: 'blue' },
    'Lifetime Creators': { icon: Star, tone: 'rose' },
    'Active Users': { icon: Smartphone, tone: 'cyan' },
    'New Sign-ups': { icon: UserPlus, tone: 'purple' },
    'Deleted Accounts': { icon: Trash2, tone: 'rose' },
  };

/** TM-3572 product-facing KPI tooltips (aligned with live metric behaviour). */
const HINT_VISITORS =
  "People who opened the app without an account during the selected period. Repeat visits are counted once where possible. Country filter is based on the visitor's location at the time of app open.";
const HINT_LIFETIME_USERS =
  'Lifetime stock of registered accounts that have never published a post. Always the all-time total — not scoped to the selected period, and no period-over-period comparison. Country filter uses Stripe onboarded country.';
const HINT_LIFETIME_CREATORS =
  'Lifetime stock of registered accounts that have published at least one post. Always the all-time total — not scoped to the selected period, and no period-over-period comparison. Country filter uses Stripe onboarded country.';
const HINT_ACTIVE_USERS =
  'Registered accounts that performed qualifying activity during the selected period (for example viewing content, engaging with posts, messaging, posting, or making a purchase). Country filter uses the country detected during that activity (not Stripe onboarded country).';
const HINT_ACTIVE_USERS_LOGIN =
  'Registered accounts with a last login during the selected period (provisional). Country filter uses Stripe onboarded country.';
const HINT_NEW_SIGNUPS =
  'Accounts that completed registration during the selected period. Country filter uses signup country (IP at registration), not Stripe Connect country.';
const HINT_DELETED =
  'Accounts that completed account deletion during the selected period. Country filter uses signup country (IP at registration), not Stripe Connect country.';

/**
 * TM-3387 Figma packing: top row Visitors/Lifetime Users/Lifetime Creators, mid row Active/New/Deleted.
 */
export function buildDashboardKpis(
  data: OverviewWithPhase4,
  _timeseries?: DashboardTimeseriesResponseDTO,
): {
  topRow: DashboardKpi[];
  bottomRow: DashboardKpi[];
} {
  const visitorsUnavailable = data.visitorsStatus === 'UNAVAILABLE';
  const visitorsComingSoon =
    !visitorsUnavailable && (data.visitorsStatus === 'COMING_SOON' || data.visitors == null);

  const withIcon = (
    label: string,
    card: Omit<DashboardKpi, 'label' | 'icon' | 'iconTone'>,
  ): DashboardKpi => {
    const meta = DASHBOARD_KPI_ICONS[label];
    return {
      label,
      ...card,
      ...(meta ? { icon: meta.icon, iconTone: meta.tone } : {}),
    };
  };

  const topRow: (DashboardKpi | null)[] = [
    visitorsUnavailable
      ? withIcon('Visitors', {
          value: '—',
          delta: null,
          unavailable: true,
          hint: 'Visitor analytics are temporarily unavailable.',
        })
      : visitorsComingSoon
        ? withIcon('Visitors', {
            value: '—',
            delta: null,
            comingSoon: true,
            hint: 'People who opened the app without an account. Coming soon.',
          })
        : withIcon('Visitors', {
            value: formatKpiCount(Number(data.visitors)),
            delta: deltaNum(data.visitorsDeltaPercent),
            hint: HINT_VISITORS,
          }),
    data.totalUsers != null
      ? withIcon('Lifetime Users', {
          value: formatKpiCount(data.totalUsers),
          // Stock figure — never period-over-period.
          delta: null,
          hint: HINT_LIFETIME_USERS,
        })
      : null,
    data.totalCreators != null
      ? withIcon('Lifetime Creators', {
          value: formatKpiCount(data.totalCreators),
          // Stock figure — never period-over-period.
          delta: null,
          hint: HINT_LIFETIME_CREATORS,
        })
      : null,
  ];

  const activeHint =
    data.activeUsersDefinition === 'QUALIFYING_ACTIVITY'
      ? HINT_ACTIVE_USERS
      : HINT_ACTIVE_USERS_LOGIN;

  const bottomRow: (DashboardKpi | null)[] = [
    data.activeUsers30d != null
      ? withIcon('Active Users', {
          value: formatKpiCount(data.activeUsers30d),
          delta: deltaNum(data.activeUsersDeltaPercent),
          hint: activeHint,
        })
      : null,
    data.newUsers30d != null
      ? withIcon('New Sign-ups', {
          value: formatKpiCount(data.newUsers30d),
          delta: deltaNum(data.newUsersDeltaPercent),
          hint: HINT_NEW_SIGNUPS,
        })
      : null,
    data.deletedAccounts != null
      ? withIcon('Deleted Accounts', {
          value: formatKpiCount(data.deletedAccounts),
          delta: deltaNum(data.deletedAccountsDeltaPercent),
          hint: HINT_DELETED,
        })
      : withIcon('Deleted Accounts', {
          value: '0',
          delta: null,
          hint: HINT_DELETED,
        }),
  ];

  return {
    topRow: topRow.filter((c): c is DashboardKpi => c != null),
    bottomRow: bottomRow.filter((c): c is DashboardKpi => c != null),
  };
}

/** Exported for chart helpers / tests. */
export function timeseriesSeriesValues(
  timeseries: DashboardTimeseriesResponseDTO | undefined,
  key: string,
): number[] {
  return seriesValues(timeseries, key);
}
